/**
 * Shared axios transport for the area's owner-hub MLP-proxy services.
 *
 * The Acquisition (Casino Ad Service) and Operations (CRM Events) data clients reach
 * their backends through the owner-hub "mlp" proxy rather than calling those service
 * hosts directly. (Lumio chat is the exception: it calls its own host directly with
 * `Bearer` auth, see `nova/…/LumioChat/endpoint.ts`.) The proxy lives on the owner-hub host
 * (`userApiUrl`) and authenticates with the SAME raw owner-hub access token as the
 * rest of the data layer. It routes by a `_svc` key and forwards to the destination:
 * a `g` (general) service stays local to the owner-hub cluster; a `c` (cluster)
 * service is forwarded to the brand's destination cluster and additionally requires
 * the brand `tenant_id`.
 *
 * Proxied URL shape (see `docs/api.owners-hub.README.md` -> "MLP proxy"):
 *
 *   {userApiUrl}/mlp/{g|c}/<service-path>?_svc=<svc>[&tenant_id=<id>]
 *
 * Two quirks this transport encapsulates so the clients stay declarative:
 *   1. The downstream service path is forwarded as a normal multi-segment path
 *      (`/campaigns/{id}/status`); each segment is `encodeURIComponent`-escaped so a
 *      reserved char in an id stays safe, while the `/` separators stay literal.
 *   2. Auth is the RAW owner-hub token (NO `Bearer` prefix) — the proxy validates it
 *      against the owner-hub session, so a `Bearer …` value makes the proxy itself
 *      401. This matches every other owner-hub call in the app.
 *
 * Session safety: every proxied call opts OUT of the auth interceptor's
 * refresh-and-teardown (`_owsNoAuthRecovery`). A 401/403 seen through the proxy can
 * originate DOWNSTREAM (a cluster service rejecting the forwarded auth) and says
 * nothing about the owner-hub session, so it must never rotate the token or log the
 * user out — it rejects straight to the caller, which owns the failure (Acquisition
 * and Operations render `N/A`). A genuine owner-hub
 * token expiry still self-heals: any NON-proxied owner-hub call refreshes the token
 * (written back to `localStorage.access_token`), which the next proxied poll reads.
 * See `src/utils/auth-interceptor.ts`.
 */
import { type AxiosRequestConfig } from 'axios';

/** `window.config` key holding the owner-hub base the proxy is mounted under. */
const PROXY_BASE_KEY = 'userApiUrl';

/** `g` = general (local to the owner-hub cluster); `c` = cluster (needs `tenant_id`). */
export type MlpScope = 'c' | 'g';

export interface MlpProxyOptions {
  /** Proxy service key, e.g. `ad` (Casino Ad), `ce` (CRM Events). */
  svc: string;
  /**
   * `c` = cluster service (forwarded to the brand's destination cluster; every call
   * REQUIRES a `tenant_id`). `g` = general service (local; no tenant).
   */
  scope: MlpScope;
  /**
   * Path prefix the destination service serves its routes under, prepended to every
   * service path before encoding (e.g. `/api/v1` for CRM Events). Defaults to none.
   */
  pathPrefix?: string;
  /** Static headers every call carries besides `Authorization` (e.g. `Accept`). */
  headers?: Record<string, string>;
}

export interface MlpProxyTransport {
  /** True when the proxy base (`userApiUrl`) is configured. */
  configured: () => boolean;
  /**
   * Build the full proxied URL for a downstream service path. The `pathPrefix` +
   * `servicePath` is forwarded as a literal multi-segment path (each segment
   * `encodeURIComponent`-escaped), and `_svc` (plus `tenant_id` for cluster scope)
   * is added to the query. Cluster scope REQUIRES a `tenantId`; general scope
   * ignores it.
   */
  url: (servicePath: string, tenantId?: number | string) => string;
  /**
   * The axios config shared by every proxied call: the RAW owner-hub token auth
   * header, the base headers, the optional abort signal and extra query params, and
   * the session-safe auth opt-out (`_owsNoAuthRecovery`).
   */
  requestConfig: (signal?: AbortSignal, params?: Record<string, unknown>) => AxiosRequestConfig;
}

/** Read a string value off the (loosely-typed) global config; `''` when absent. */
const readConfig = (key: string): string => {
  const value = (window.config as unknown as Record<string, unknown> | undefined)?.[key];
  return typeof value === 'string' ? value : '';
};

/** The owner-hub proxy base (`userApiUrl`) with any trailing slashes stripped. */
const proxyBase = (): string => readConfig(PROXY_BASE_KEY).replace(/\/+$/, '');

/**
 * Build the transport (URL builder + request config) for one MLP-proxy service.
 * See the module header for the per-service options each client passes.
 */
export const createMlpProxyTransport = (options: MlpProxyOptions): MlpProxyTransport => {
  const { svc, scope, pathPrefix = '', headers: baseHeaders } = options;

  const configured = (): boolean => proxyBase() !== '';

  const url = (servicePath: string, tenantId?: number | string): string => {
    // The full destination path (service prefix + path), forwarded as a literal
    // multi-segment path; each segment is escaped but the `/` separators stay literal.
    const rel = `${pathPrefix}${servicePath}`.replace(/^\/+/, '');
    const encoded = rel.split('/').map(encodeURIComponent).join('/');
    const query = new URLSearchParams({ _svc: svc });
    if (scope === 'c' && tenantId != null && tenantId !== '') {
      query.set('tenant_id', String(tenantId));
    }
    return `${proxyBase()}/mlp/${scope}/${encoded}?${query.toString()}`;
  };

  const authHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = { ...baseHeaders };
    // The RAW owner-hub access token (no `Bearer`), exactly like every other
    // owner-hub call; read from localStorage so this stays free of the redux
    // Provider (these clients can render outside one in tests) and always reads the
    // freshest token (a refresh writes it straight back there).
    const token = localStorage.getItem('access_token') || '';
    if (token) headers.Authorization = token;
    return headers;
  };

  const requestConfig = (signal?: AbortSignal, params?: Record<string, unknown>): AxiosRequestConfig => ({
    headers: authHeaders(),
    // A proxied 401/403 (often a downstream service rejecting the forwarded auth) is
    // not an owner-hub-session signal: never refresh or tear down — reject to caller.
    _owsNoAuthRecovery: true,
    signal,
    ...(params ? { params } : {}),
  });

  return { configured, url, requestConfig };
};
